import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAchievements } from '../api/hooks';
import { BackIcon, ChevronRightIcon } from '../components/icons';
import { AchIcon, ShieldIcon } from '../components/achievements/icons';
import { Medallion } from '../components/achievements/Medallion';
import { ErrorView } from '../components/StateViews';
import { backButton } from '../telegram';
import { track } from '../analytics';

/**
 * Сводка достижений (K1): герой очков + лояльность, «Недавние», категории.
 * Работает и как гостевой каталог (/players/:pid/achievements) — тогда без
 * приватных элементов (их вырезает сервер, isOwner в DTO).
 */
export function AchievementsScreen() {
  const navigate = useNavigate();
  const { pid: rawPid } = useParams();
  const pid = rawPid === undefined ? null : Number(rawPid);
  const summary = useAchievements(pid);

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  const base = pid === null ? '/achievements' : `/players/${pid}/achievements`;

  if (summary.isLoading) {
    return (
      <div className="ach-screen">
        <div className="skeleton" style={{ height: 92, borderRadius: 18, marginBottom: 16 }} />
        <div className="skeleton" style={{ height: 80, borderRadius: 14, marginBottom: 16 }} />
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="skeleton" style={{ height: 44, borderRadius: 12, marginBottom: 8 }} />
        ))}
      </div>
    );
  }
  if (summary.isError || !summary.data) {
    return (
      <div className="ach-screen">
        <ErrorView message={(summary.error as Error)?.message} />
      </div>
    );
  }

  const s = summary.data;
  const pct = s.totalCount > 0 ? Math.round((s.doneCount / s.totalCount) * 100) : 0;

  return (
    <div className="ach-screen">
      <div className="subhead ach-head">
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Назад">
          <BackIcon />
        </button>
        <div className="title">Достижения</div>
      </div>

      <div className="ach-hero">
        <div className="ach-hero-shield">
          {s.loyalty?.icon ? <AchIcon id={s.loyalty.icon} /> : <ShieldIcon />}
        </div>
        <div className="ach-hero-main">
          <div className="ach-hero-points">{s.points}</div>
          <div className="ach-hero-sub">
            очков достижений · {s.doneCount} из {s.totalCount} выполнено
          </div>
          <div className="ach-hero-bar">
            <i style={{ width: `${pct}%` }} />
          </div>
          {s.loyalty && s.isOwner && (
            <div className="ach-hero-loyalty">
              {s.loyalty.icon && <AchIcon id={s.loyalty.icon} />}
              <span>
                {s.loyalty.name}
                {s.loyalty.discountPct > 0 ? ` · скидка ${s.loyalty.discountPct}%` : ''}
                {s.loyalty.next ? ` · до «${s.loyalty.next.name}» ${s.loyalty.next.threshold - s.points}` : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      {s.recent.length > 0 && (
        <>
          <div className="ach-overline ach-section">
            <span>Недавние</span>
          </div>
          <div className="ach-recent">
            {s.recent.map((r) => (
              <Link
                key={`${r.id}-${r.at}`}
                className="ach-recent-item"
                to={`${base}/${r.id}`}
                onClick={() => track('achievement_open', { id: r.id, from: 'recent' })}
              >
                <Medallion icon={r.icon} level={r.level} maxLevel={r.level} masked={r.masked}
                  ring={false} size={46} />
                <span>{r.name}</span>
              </Link>
            ))}
          </div>
        </>
      )}

      <div className="ach-overline ach-section">
        <span>Категории</span>
      </div>
      <div className="ach-cats">
        {s.categories.map((c) => (
          <CategoryRow key={c.id} base={base} id={String(c.id)} name={c.name} done={c.done}
            total={c.total} seasonal={false} />
        ))}
        {s.seasonal && (
          <CategoryRow
            base={base}
            id="seasonal"
            name={
              s.seasonal.daysLeft !== null
                ? `Сезонные · ещё ${s.seasonal.daysLeft} дн.`
                : 'Сезонные'
            }
            done={s.seasonal.done}
            total={s.seasonal.total}
            seasonal
          />
        )}
      </div>
      <div className="ach-fade" />
    </div>
  );
}

function CategoryRow({
  base,
  id,
  name,
  done,
  total,
  seasonal,
}: {
  base: string;
  id: string;
  name: string;
  done: number;
  total: number;
  seasonal: boolean;
}) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <Link
      className={`ach-cat-row${seasonal ? ' ach-cat-row--season' : ''}`}
      to={`${base}/categories/${id}`}
      onClick={() => track('achievement_category_open', { category: id })}
    >
      <div className="ach-cat-main">
        <div className="ach-cat-top">
          <span className="nm">{name}</span>
          <span className="cnt">
            {done}/{total}
          </span>
        </div>
        <div className="ach-cat-bar">
          <i style={{ width: `${pct}%` }} />
        </div>
      </div>
      <span className="ach-cat-chev">
        <ChevronRightIcon />
      </span>
    </Link>
  );
}
